import React, { useEffect, useMemo, useState } from "react";
import { toast, Toaster } from "sonner";
import { Link } from "react-router-dom";
import {
  Save, Send, RefreshCw, CheckCircle2, XCircle, AlertCircle,
  Users, FileText, Palette, Bell, Plug, Shield, ExternalLink, Mail, Timer, Briefcase,
  CreditCard, Copy,
} from "lucide-react";
import DashboardShell from "../../components/layout/DashboardShell";
import EmailTemplatesTab from "../../components/admin/EmailTemplatesTab";
import SlaLadderTab from "../../components/admin/SlaLadderTab";
import AdminBrandCard from "../../components/admin/AdminBrandCard";
import { Card, Pill } from "../../components/common/Primitives";
import { Button } from "../../components/ui/button";
import api, { formatApiError } from "../../lib/api";
import { formatDateTime } from "../../lib/datetime";

// ---------------------------------------------------------------------------
// Small building blocks (kept local to avoid churn on shared primitives)
// ---------------------------------------------------------------------------
function SectionHeader({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex items-start gap-3 mb-5">
      <div className="mt-0.5 w-9 h-9 rounded-md bg-gold/15 border border-gold/40 flex items-center justify-center">
        <Icon size={17} className="text-[#8A6600]" />
      </div>
      <div>
        <div className="font-heading text-ink text-lg leading-tight">{title}</div>
        {subtitle ? <div className="text-xs text-ink-muted mt-0.5">{subtitle}</div> : null}
      </div>
    </div>
  );
}

function Toggle({ checked, onChange, label, hint, testId, disabled }) {
  return (
    <label className={`flex items-start justify-between gap-4 py-3 border-b border-navy/[0.08] last:border-0 ${disabled ? "opacity-60" : ""}`}>
      <div className="pr-2">
        <div className="text-ink font-heading text-sm">{label}</div>
        {hint ? <div className="text-xs text-ink-muted mt-0.5">{hint}</div> : null}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        data-testid={testId}
        className={`shrink-0 w-11 h-6 rounded-full transition-colors relative outline-none focus:ring-2 focus:ring-gold/40 ${checked ? "bg-navy" : "bg-navy/20"}`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${checked ? "translate-x-5" : ""}`}
        />
      </button>
    </label>
  );
}

function LabeledInput({ label, hint, value, onChange, placeholder, type = "text", testId }) {
  return (
    <label className="block">
      <div className="overline mb-1.5">{label}</div>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full bg-white border border-navy/15 text-ink px-3 py-2.5 text-sm font-body focus:border-gold focus:ring-2 focus:ring-gold/30 outline-none transition-all rounded-md"
      />
      {hint ? <div className="text-xs text-ink-muted mt-1">{hint}</div> : null}
    </label>
  );
}

function StatusDot({ status }) {
  const map = {
    configured: { tone: "success", Icon: CheckCircle2, label: "Configured" },
    not_configured: { tone: "danger", Icon: XCircle, label: "Not configured" },
    mock: { tone: "warn", Icon: AlertCircle, label: "Mock mode" },
  };
  const cfg = map[status] || map.not_configured;
  return (
    <span className="inline-flex items-center gap-1.5">
      <cfg.Icon size={14} className={
        cfg.tone === "success" ? "text-emerald-600" :
        cfg.tone === "warn" ? "text-amber-600" : "text-rose-600"
      } />
      <Pill tone={cfg.tone}>{cfg.label}</Pill>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
const TABS = [
  { id: "system", label: "System", icon: Shield },
  { id: "brand", label: "Brand", icon: Palette },
  { id: "notifications", label: "Notifications", icon: Bell },
  { id: "kyc", label: "KYC", icon: FileText },
  { id: "emails", label: "Emails", icon: Mail },
  { id: "sla_ladder", label: "SLA Ladder", icon: Timer },
  { id: "payments", label: "Payments", icon: CreditCard },
  { id: "integrations", label: "Integrations", icon: Plug },
  { id: "roles", label: "Roles", icon: Users },
  { id: "legal", label: "Legal / CMS", icon: FileText },
];

export default function AdminSettings() {
  const [tab, setTab] = useState("system");
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [integrations, setIntegrations] = useState([]);
  const [roles, setRoles] = useState({ roles: [], total: 0 });
  const [testEmailTo, setTestEmailTo] = useState("");
  const [testEmailSending, setTestEmailSending] = useState(false);

  const load = async () => {
    try {
      const [s, i, r] = await Promise.all([
        api.get("/admin/settings"),
        api.get("/admin/settings/integrations"),
        api.get("/admin/settings/roles-summary"),
      ]);
      setSettings(s.data);
      setDraft(JSON.parse(JSON.stringify(s.data)));
      setIntegrations(i.data.integrations || []);
      setRoles(r.data || { roles: [], total: 0 });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { load(); }, []);

  const dirty = useMemo(() => {
    if (!settings || !draft) return false;
    return JSON.stringify(settings) !== JSON.stringify(draft);
  }, [settings, draft]);

  const patch = (updater) => setDraft((d) => (d ? updater({ ...d }) : d));
  const patchBrand = (field, value) => patch((d) => ({ ...d, brand: { ...(d.brand || {}), [field]: value } }));
  const patchNotifications = (field, value) => patch((d) => ({ ...d, notifications: { ...(d.notifications || {}), [field]: value } }));
  const patchKyc = (field, value) => patch((d) => ({ ...d, kyc: { ...(d.kyc || {}), [field]: value } }));
  const patchPortfolio = (field, value) => patch((d) => ({ ...d, portfolio: { ...(d.portfolio || {}), [field]: value } }));

  const save = async () => {
    if (!draft || !dirty) return;
    setSaving(true);
    try {
      const payload = {
        maintenance_mode: draft.maintenance_mode,
        signups_open: draft.signups_open,
        allow_startup_signups: draft.allow_startup_signups,
        allow_investor_signups: draft.allow_investor_signups,
        brand: draft.brand,
        notifications: draft.notifications,
        kyc: draft.kyc,
        portfolio: draft.portfolio,
      };
      const res = await api.patch("/admin/settings", payload);
      setSettings(res.data);
      setDraft(JSON.parse(JSON.stringify(res.data)));
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const sendTestEmail = async () => {
    if (!testEmailTo.trim()) {
      toast.error("Enter a recipient email");
      return;
    }
    setTestEmailSending(true);
    try {
      await api.post("/admin/settings/integrations/test-email", { to: testEmailTo.trim() });
      toast.success(`Test email dispatched to ${testEmailTo.trim()}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setTestEmailSending(false);
    }
  };

  if (!draft) {
    return (
      <DashboardShell title="Settings" subtitle="ADMIN · SYSTEM">
        <Card><div className="text-ink-muted text-sm">Loading settings…</div></Card>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell title="Settings" subtitle="ADMIN · SYSTEM">
      <Toaster position="top-right" richColors />

      {/* Brand upload — logo + favicon apply immediately across every surface */}
      <AdminBrandCard />

      {/* Header actions bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5" data-testid="settings-header-bar">
        <div className="text-ink-soft text-sm">
          Configure platform-wide toggles, brand, and integrations. Changes take effect immediately.
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="settings-refresh-btn">
            <RefreshCw size={14} className="mr-1.5" /> Refresh
          </Button>
          <Button
            variant="default"
            size="sm"
            disabled={!dirty || saving}
            onClick={save}
            data-testid="settings-save-btn"
            className="bg-navy hover:bg-[#256E58] text-white"
          >
            <Save size={14} className="mr-1.5" /> {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 border-b border-navy/10 mb-6" data-testid="settings-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`settings-tab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-heading transition-colors border-b-2 -mb-px ${
              tab === t.id
                ? "border-gold text-ink"
                : "border-transparent text-ink-muted hover:text-ink"
            }`}
          >
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      {/* ---- SYSTEM ---- */}
      {tab === "system" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="settings-panel-system">
          <Card>
            <SectionHeader icon={Shield} title="Access & Availability"
              subtitle="Control who can register and whether the platform is in maintenance." />
            <Toggle
              testId="toggle-maintenance-mode"
              checked={!!draft.maintenance_mode}
              onChange={(v) => patch((d) => ({ ...d, maintenance_mode: v }))}
              label="Maintenance mode"
              hint="When on, this signals downstream services / banners to show a maintenance state."
            />
            <Toggle
              testId="toggle-signups-open"
              checked={!!draft.signups_open}
              onChange={(v) => patch((d) => ({ ...d, signups_open: v }))}
              label="Signups open"
              hint="Master switch for new user registration."
            />
            <Toggle
              testId="toggle-startup-signups"
              checked={!!draft.allow_startup_signups}
              onChange={(v) => patch((d) => ({ ...d, allow_startup_signups: v }))}
              label="Allow startup signups"
              hint="If off, founders cannot self-register."
              disabled={!draft.signups_open}
            />
            <Toggle
              testId="toggle-investor-signups"
              checked={!!draft.allow_investor_signups}
              onChange={(v) => patch((d) => ({ ...d, allow_investor_signups: v }))}
              label="Allow investor signups"
              hint="If off, investors cannot self-register."
              disabled={!draft.signups_open}
            />
          </Card>

          <Card>
            <SectionHeader icon={FileText} title="Meta"
              subtitle="Last updated + audit trail." />
            <div className="space-y-3 text-sm">
              <div className="flex justify-between border-b border-navy/[0.08] py-2.5">
                <span className="text-ink-muted">Last updated</span>
                <span className="font-mono text-ink text-xs">
                  {settings?.updated_at ? formatDateTime(settings.updated_at) : "—"}
                </span>
              </div>
              <div className="flex justify-between border-b border-navy/[0.08] py-2.5">
                <span className="text-ink-muted">Updated by</span>
                <span className="font-mono text-ink text-xs">{settings?.updated_by || "—"}</span>
              </div>
              <div className="flex justify-between py-2.5">
                <span className="text-ink-muted">Audit logs</span>
                <Link to="/admin/audit-logs" className="text-[#8A6600] hover:underline text-xs flex items-center gap-1">
                  View activity <ExternalLink size={12} />
                </Link>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* ---- BRAND ---- */}
      {tab === "brand" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="settings-panel-brand">
          <Card>
            <SectionHeader icon={Palette} title="Brand identity"
              subtitle="Shown on the platform and outbound emails." />
            <div className="space-y-4">
              <LabeledInput
                label="Company legal name"
                value={draft.brand?.company_legal_name}
                onChange={(v) => patchBrand("company_legal_name", v)}
                placeholder="Ultiwise Ventures Private Limited"
                testId="input-company-legal-name"
              />
              <LabeledInput
                label="Company city"
                value={draft.brand?.company_city}
                onChange={(v) => patchBrand("company_city", v)}
                placeholder="Noida"
                testId="input-company-city"
              />
              <LabeledInput
                label="Footer tagline"
                value={draft.brand?.footer_tagline}
                onChange={(v) => patchBrand("footer_tagline", v)}
                placeholder="Powered by CorpZo"
                testId="input-footer-tagline"
              />
              <LabeledInput
                label="Logo URL"
                value={draft.brand?.logo_url}
                onChange={(v) => patchBrand("logo_url", v)}
                placeholder="https://cdn.venturaz.com/logo.png"
                hint="Public URL to your logo asset (PNG/SVG)."
                testId="input-logo-url"
              />
              <div>
                <div className="overline mb-1.5">Primary color</div>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={draft.brand?.primary_color || "#0F3D2E"}
                    onChange={(e) => patchBrand("primary_color", e.target.value)}
                    className="w-14 h-10 rounded border border-navy/15 cursor-pointer"
                    data-testid="input-primary-color"
                  />
                  <input
                    type="text"
                    value={draft.brand?.primary_color || ""}
                    onChange={(e) => patchBrand("primary_color", e.target.value)}
                    placeholder="#0F3D2E"
                    className="flex-1 bg-white border border-navy/15 text-ink px-3 py-2.5 text-sm font-mono focus:border-gold outline-none rounded-md"
                  />
                </div>
              </div>
            </div>
          </Card>

          <Card>
            <SectionHeader icon={Palette} title="Preview" subtitle="Live preview of your brand strip." />
            <div
              className="rounded-lg p-6 border border-navy/10"
              style={{ background: `linear-gradient(135deg, ${draft.brand?.primary_color || "#0F3D2E"} 0%, #256E58 100%)` }}
            >
              {draft.brand?.logo_url ? (
                <img src={draft.brand.logo_url} alt="Brand" className="h-8 mb-4" onError={(e) => { e.currentTarget.style.display = "none"; }} />
              ) : (
                <div className="font-heading text-white text-xl mb-4">VenturaZ</div>
              )}
              <div className="text-white/85 text-sm">
                {draft.brand?.company_legal_name || "Ultiwise Ventures Private Limited"} · {draft.brand?.company_city || "Noida"}
              </div>
              <div className="text-white/60 text-xs mt-1 font-mono">{draft.brand?.footer_tagline || "Powered by CorpZo"}</div>
            </div>
          </Card>
        </div>
      )}

      {/* ---- NOTIFICATIONS ---- */}
      {tab === "notifications" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="settings-panel-notifications">
          <Card>
            <SectionHeader icon={Bell} title="System alerts"
              subtitle="Where high-signal platform events are sent." />
            <div className="space-y-4">
              <LabeledInput
                label="System alert email"
                type="email"
                value={draft.notifications?.system_alert_email}
                onChange={(v) => patchNotifications("system_alert_email", v)}
                placeholder="ops@venturaz.com"
                hint="Ops mailbox for cron failures and critical alerts."
                testId="input-system-alert-email"
              />
              <div className="pt-2">
                <Toggle
                  testId="toggle-notify-new-signup"
                  checked={!!draft.notifications?.notify_on_new_signup}
                  onChange={(v) => patchNotifications("notify_on_new_signup", v)}
                  label="Notify on new signup"
                  hint="Send an email to the alert mailbox when a new user registers."
                />
                <Toggle
                  testId="toggle-notify-deal-close"
                  checked={!!draft.notifications?.notify_on_deal_close}
                  onChange={(v) => patchNotifications("notify_on_deal_close", v)}
                  label="Notify on deal close"
                  hint="Send an email when a deal room is marked closed."
                />
              </div>
            </div>
          </Card>

          <Card>
            <SectionHeader icon={Send} title="Send a test email"
              subtitle="Verifies Resend is wired up correctly." />
            <div className="space-y-3">
              <LabeledInput
                label="Recipient"
                type="email"
                value={testEmailTo}
                onChange={setTestEmailTo}
                placeholder="you@example.com"
                testId="input-test-email-to"
              />
              <Button
                onClick={sendTestEmail}
                disabled={testEmailSending}
                data-testid="btn-send-test-email"
                className="bg-navy hover:bg-[#256E58] text-white w-full"
              >
                <Send size={14} className="mr-1.5" /> {testEmailSending ? "Sending…" : "Send test email"}
              </Button>
              <div className="text-xs text-ink-muted">
                Requires <span className="font-mono">RESEND_API_KEY</span> in backend env.
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* ---- KYC ---- */}
      {tab === "kyc" && (
        <div data-testid="settings-panel-kyc">
          <Card>
            <SectionHeader icon={FileText} title="Startup KYC requirements"
              subtitle="Document categories founders must upload before /startup/kyc/submit is accepted. Comma-separated, one per pill." />
            <div className="space-y-4 max-w-2xl">
              <div>
                <label className="text-xs font-heading text-ink-muted uppercase tracking-widest">Required document categories</label>
                <input
                  className="w-full px-3 py-2 mt-1 rounded-md border border-navy/15 bg-white text-sm text-ink"
                  value={(draft.kyc?.required_docs || []).join(", ")}
                  onChange={(e) => patchKyc("required_docs", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                  placeholder="pan_card, cin_certificate, bank_statement, founder_id"
                  data-testid="input-kyc-required-docs"
                />
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="kyc-required-docs-pills">
                  {(draft.kyc?.required_docs || []).map((c) => (
                    <span key={c} className="text-[11px] font-mono px-2 py-0.5 rounded-full bg-navy/[0.05] border border-navy/10 text-ink">
                      {c}
                    </span>
                  ))}
                  {(draft.kyc?.required_docs || []).length === 0 && (
                    <span className="text-xs text-ink-faint">No categories configured — KYC will not enforce docs.</span>
                  )}
                </div>
                <div className="text-[11px] text-ink-muted mt-1">Categories must match what founders select when uploading in the Document Vault.</div>
              </div>
              <div>
                <label className="text-xs font-heading text-ink-muted uppercase tracking-widest">Minimum document count (extra safety)</label>
                <input
                  type="number"
                  min={0}
                  className="w-32 px-3 py-2 mt-1 rounded-md border border-navy/15 bg-white text-sm text-ink"
                  value={draft.kyc?.min_doc_count ?? 0}
                  onChange={(e) => patchKyc("min_doc_count", Number(e.target.value))}
                  data-testid="input-kyc-min-doc-count"
                />
                <div className="text-[11px] text-ink-muted mt-1">Enforces a floor even if a founder marks the same category twice.</div>
              </div>
              <div>
                <label className="text-xs font-heading text-ink-muted uppercase tracking-widest">Reviewer SLA (hours)</label>
                <input
                  type="number"
                  min={1}
                  className="w-32 px-3 py-2 mt-1 rounded-md border border-navy/15 bg-white text-sm text-ink"
                  value={draft.kyc?.sla_hours ?? 48}
                  onChange={(e) => patchKyc("sla_hours", Number(e.target.value))}
                  data-testid="input-kyc-sla-hours"
                />
                <div className="text-[11px] text-ink-muted mt-1">Any &ldquo;Under Review&rdquo; KYC older than this shows a red SLA pill on the queue.</div>
              </div>
            </div>
          </Card>

          <Card className="mt-6">
            <SectionHeader
              icon={FileText}
              title="Reviewer note templates"
              subtitle="Dropdown options in the KYC drawer that auto-fill the decision note. Reorder freely — order = display order in the dropdown."
            />
            <ReviewerTemplatesEditor
              value={draft.kyc?.reviewer_templates || []}
              onChange={(next) => patchKyc("reviewer_templates", next)}
            />
          </Card>

          <Card className="mt-6">
            <SectionHeader
              icon={FileText}
              title="Passport redaction presets"
              subtitle="Quick-redact chips shown above the passport preview. Coords are stored as page-percentages so they survive PDF regeneration. Use the Passport Redactor to eyeball coordinates before saving here."
            />
            <PassportPresetsEditor
              value={draft.kyc?.passport_presets || []}
              onChange={(next) => patchKyc("passport_presets", next)}
            />
          </Card>

          <Card className="mt-6">
            <SectionHeader
              icon={Briefcase}
              title="Portfolio concentration alerts"
              subtitle="Warn investors when too much capital sits in one sector."
            />
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-heading text-ink">Sector concentration alert</div>
                  <div className="text-[11px] text-ink-muted mt-0.5">
                    When enabled, investors see a banner on their dashboard whenever a single sector exceeds the threshold.
                  </div>
                </div>
                <label className="inline-flex items-center cursor-pointer" data-testid="toggle-sector-alert">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={draft.portfolio?.sector_alert_enabled !== false}
                    onChange={(e) => patchPortfolio("sector_alert_enabled", e.target.checked)}
                    data-testid="input-sector-alert-enabled"
                  />
                  <div className="relative w-11 h-6 bg-navy/15 peer-checked:bg-emerald-600 rounded-full transition-colors">
                    <div className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow peer-checked:translate-x-5 transition-transform" />
                  </div>
                </label>
              </div>
              <div>
                <label className="text-xs font-heading text-ink-muted uppercase tracking-widest">Threshold (%)</label>
                <input
                  type="number"
                  min={1}
                  max={100}
                  disabled={draft.portfolio?.sector_alert_enabled === false}
                  className="w-32 px-3 py-2 mt-1 rounded-md border border-navy/15 bg-white text-sm text-ink disabled:opacity-50"
                  value={draft.portfolio?.sector_alert_threshold_pct ?? 40}
                  onChange={(e) => patchPortfolio("sector_alert_threshold_pct", Number(e.target.value))}
                  data-testid="input-sector-alert-threshold"
                />
                <div className="text-[11px] text-ink-muted mt-1">
                  Fire the alert when any single sector holds more than this % of the investor&#39;s total capital.
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}


      {tab === "emails" && (
        <EmailTemplatesTab data-testid="settings-panel-emails" />
      )}

      {tab === "sla_ladder" && (
        <SlaLadderTab data-testid="settings-panel-sla_ladder" />
      )}

      {tab === "payments" && (
        <PaymentsTab />
      )}

      {/* ---- INTEGRATIONS ---- */}
      {tab === "integrations" && (
        <div data-testid="settings-panel-integrations">
          <Card>
            <SectionHeader icon={Plug} title="Third-party integrations"
              subtitle="Read-only status. Keys are managed via backend .env." />
            <div className="divide-y divide-navy/[0.08]">
              {integrations.map((i) => (
                <div key={i.id} className="py-4 flex flex-wrap items-start justify-between gap-3" data-testid={`integration-row-${i.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-heading text-ink text-sm mb-1">{i.name}</div>
                    <div className="text-xs text-ink-muted">{i.detail}</div>
                    <div className="text-[11px] text-ink-faint font-mono mt-1">{i.env_var}</div>
                  </div>
                  <StatusDot status={i.status} />
                </div>
              ))}
              {integrations.length === 0 && (
                <div className="py-6 text-sm text-ink-muted text-center">No integrations detected.</div>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* ---- ROLES ---- */}
      {tab === "roles" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="settings-panel-roles">
          <Card>
            <SectionHeader icon={Users} title="Role distribution"
              subtitle={`Total users: ${roles.total}`} />
            <div className="divide-y divide-navy/[0.08]">
              {roles.roles.map((r) => (
                <div key={r.role} className="flex items-center justify-between py-2.5" data-testid={`role-row-${r.role}`}>
                  <div className="flex items-center gap-2">
                    <Pill tone={
                      r.role === "super_admin" ? "gold" :
                      r.role === "admin" || r.role === "manager" || r.role === "agent" ? "muted" :
                      "success"
                    }>{r.role.replace("_", " ")}</Pill>
                  </div>
                  <div className="font-mono text-sm text-ink">{r.count}</div>
                </div>
              ))}
            </div>
          </Card>

          <Card>
            <SectionHeader icon={Users} title="Manage users"
              subtitle="Add, edit, or change roles for platform users." />
            <div className="space-y-3">
              <Link to="/admin/users" className="block">
                <Button variant="outline" className="w-full justify-between" data-testid="link-manage-users">
                  <span>Open user management</span>
                  <ExternalLink size={14} />
                </Button>
              </Link>
              <Link to="/admin/managers" className="block">
                <Button variant="outline" className="w-full justify-between" data-testid="link-manage-managers">
                  <span>Managers &amp; agents</span>
                  <ExternalLink size={14} />
                </Button>
              </Link>
              <Link to="/admin/audit-logs" className="block">
                <Button variant="outline" className="w-full justify-between" data-testid="link-audit-logs">
                  <span>Audit logs</span>
                  <ExternalLink size={14} />
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      )}

      {/* ---- LEGAL / CMS ---- */}
      {tab === "legal" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="settings-panel-legal">
          <Card>
            <SectionHeader icon={FileText} title="Legal & policy pages"
              subtitle="Manage public-facing legal content via the CMS." />
            <div className="space-y-2">
              {[
                { slug: "terms", label: "Terms of Service" },
                { slug: "privacy", label: "Privacy Policy" },
                { slug: "compliance", label: "Compliance" },
                { slug: "disclaimer", label: "Disclaimer" },
                { slug: "cookie-policy", label: "Cookie Policy" },
              ].map((p) => (
                <Link key={p.slug} to={`/admin/cms`} className="block">
                  <div
                    className="flex items-center justify-between px-3 py-2.5 border border-navy/10 rounded-md hover:border-gold hover:bg-gold/5 transition-colors"
                    data-testid={`cms-link-${p.slug}`}
                  >
                    <div>
                      <div className="text-ink font-heading text-sm">{p.label}</div>
                      <div className="text-[11px] font-mono text-ink-muted">/{p.slug}</div>
                    </div>
                    <ExternalLink size={14} className="text-ink-muted" />
                  </div>
                </Link>
              ))}
            </div>
          </Card>

          <Card>
            <SectionHeader icon={FileText} title="CMS console"
              subtitle="Full editor for all CMS pages." />
            <div className="text-sm text-ink-soft mb-4">
              Edit any public-facing content (terms, privacy, compliance, disclaimer, cookie policy) with markdown, SEO
              metadata, and versioning.
            </div>
            <Link to="/admin/cms">
              <Button className="bg-navy hover:bg-[#256E58] text-white w-full" data-testid="btn-open-cms">
                Open CMS <ExternalLink size={14} className="ml-1.5" />
              </Button>
            </Link>
          </Card>
        </div>
      )}
    </DashboardShell>
  );
}

function ReviewerTemplatesEditor({ value, onChange }) {
  const STATUS_OPTIONS = ["Approved", "Rejected", "More Information Required"];
  const updateAt = (idx, patch) => {
    const next = value.map((t, i) => (i === idx ? { ...t, ...patch } : t));
    onChange(next);
  };
  const removeAt = (idx) => onChange(value.filter((_, i) => i !== idx));
  const moveTo = (idx, dir) => {
    const next = [...value];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };
  const add = () => onChange([...value, {
    label: "", status: "More Information Required", note: "",
  }]);
  return (
    <div className="space-y-3" data-testid="reviewer-templates-editor">
      {value.length === 0 && (
        <div className="text-xs text-ink-faint italic px-3 py-2 border border-dashed border-navy/15 rounded-md">
          No templates configured — reviewers will see the built-in defaults.
        </div>
      )}
      {value.map((t, i) => (
        <div key={i} className="border border-navy/10 rounded-md p-3 bg-white space-y-2" data-testid={`reviewer-template-${i}`}>
          <div className="flex items-center gap-2">
            <input
              value={t.label}
              onChange={(e) => updateAt(i, { label: e.target.value })}
              placeholder="e.g. Missing PAN"
              className="flex-1 text-sm border border-navy/15 rounded-md px-2 py-1.5 outline-none focus:border-emerald-400"
              data-testid={`rt-label-${i}`}
            />
            <select
              value={t.status}
              onChange={(e) => updateAt(i, { status: e.target.value })}
              className="text-xs border border-navy/15 rounded-md px-2 py-1.5 bg-white"
              data-testid={`rt-status-${i}`}
            >
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <button
              onClick={() => moveTo(i, -1)}
              disabled={i === 0}
              className="text-xs px-2 py-1.5 rounded border border-navy/15 disabled:opacity-30 hover:bg-navy/[0.04]"
              title="Move up"
              data-testid={`rt-up-${i}`}
            >↑</button>
            <button
              onClick={() => moveTo(i, 1)}
              disabled={i === value.length - 1}
              className="text-xs px-2 py-1.5 rounded border border-navy/15 disabled:opacity-30 hover:bg-navy/[0.04]"
              title="Move down"
              data-testid={`rt-down-${i}`}
            >↓</button>
            <button
              onClick={() => removeAt(i)}
              className="text-xs px-2 py-1.5 rounded border border-rose-300 text-rose-700 hover:bg-rose-50"
              title="Remove template"
              data-testid={`rt-remove-${i}`}
            >×</button>
          </div>
          <textarea
            value={t.note}
            onChange={(e) => updateAt(i, { note: e.target.value })}
            rows={3}
            placeholder="Note that will auto-fill the reviewer textarea. Written directly to the applicant."
            className="w-full text-sm border border-navy/15 rounded-md px-2 py-1.5 outline-none focus:border-emerald-400"
            data-testid={`rt-note-${i}`}
          />
        </div>
      ))}
      <button
        onClick={add}
        className="text-xs px-3 py-1.5 rounded border border-emerald-400 text-emerald-800 hover:bg-emerald-50 font-heading"
        data-testid="rt-add"
      >
        + Add template
      </button>
    </div>
  );
}

function PassportPresetsEditor({ value, onChange }) {
  const updateAt = (idx, patch) => onChange(value.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  const removeAt = (idx) => onChange(value.filter((_, i) => i !== idx));
  const moveTo = (idx, dir) => {
    const next = [...value];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };
  const add = () => onChange([...value, {
    key: `preset_${Date.now()}`,
    label: "",
    hint: "",
    boxes: [{ page: 1, x_pct: 33, y_pct: 25, w_pct: 55, h_pct: 3.2 }],
  }]);
  const updateBox = (pIdx, bIdx, patch) => {
    const preset = value[pIdx];
    const nextBoxes = preset.boxes.map((b, i) => (i === bIdx ? { ...b, ...patch } : b));
    updateAt(pIdx, { boxes: nextBoxes });
  };
  const addBox = (pIdx) => {
    const preset = value[pIdx];
    updateAt(pIdx, { boxes: [...preset.boxes, { page: 1, x_pct: 33, y_pct: 25, w_pct: 55, h_pct: 3.2 }] });
  };
  const removeBox = (pIdx, bIdx) => {
    const preset = value[pIdx];
    updateAt(pIdx, { boxes: preset.boxes.filter((_, i) => i !== bIdx) });
  };
  return (
    <div className="space-y-3" data-testid="passport-presets-editor">
      {value.length === 0 && (
        <div className="text-xs text-ink-faint italic px-3 py-2 border border-dashed border-navy/15 rounded-md">
          No presets configured — reviewers will see the built-in defaults.
        </div>
      )}
      {value.map((p, i) => (
        <div key={i} className="border border-navy/10 rounded-md p-3 bg-white space-y-2" data-testid={`passport-preset-${i}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              value={p.key}
              onChange={(e) => updateAt(i, { key: e.target.value.replace(/[^a-z0-9_]/gi, "_").toLowerCase() })}
              placeholder="preset_key"
              className="w-40 text-xs font-mono border border-navy/15 rounded-md px-2 py-1.5 outline-none focus:border-emerald-400"
              data-testid={`pp-key-${i}`}
            />
            <input
              value={p.label}
              onChange={(e) => updateAt(i, { label: e.target.value })}
              placeholder="e.g. Firm name"
              className="flex-1 min-w-[10rem] text-sm border border-navy/15 rounded-md px-2 py-1.5 outline-none focus:border-emerald-400"
              data-testid={`pp-label-${i}`}
            />
            <button onClick={() => moveTo(i, -1)} disabled={i === 0}
              className="text-xs px-2 py-1.5 rounded border border-navy/15 disabled:opacity-30 hover:bg-navy/[0.04]"
              data-testid={`pp-up-${i}`}>↑</button>
            <button onClick={() => moveTo(i, 1)} disabled={i === value.length - 1}
              className="text-xs px-2 py-1.5 rounded border border-navy/15 disabled:opacity-30 hover:bg-navy/[0.04]"
              data-testid={`pp-down-${i}`}>↓</button>
            <button onClick={() => removeAt(i)}
              className="text-xs px-2 py-1.5 rounded border border-rose-300 text-rose-700 hover:bg-rose-50"
              data-testid={`pp-remove-${i}`}>×</button>
          </div>
          <input
            value={p.hint || ""}
            onChange={(e) => updateAt(i, { hint: e.target.value })}
            placeholder="Tooltip shown on the chip (optional)"
            className="w-full text-xs border border-navy/15 rounded-md px-2 py-1.5 outline-none focus:border-emerald-400"
            data-testid={`pp-hint-${i}`}
          />
          <div className="pt-1 border-t border-navy/[0.05]">
            <div className="text-[10px] uppercase tracking-widest text-ink-muted font-heading mb-1">Boxes (page-percentages)</div>
            <div className="space-y-1.5">
              {p.boxes.map((b, bi) => (
                <div key={bi} className="grid grid-cols-6 gap-1.5 items-center" data-testid={`pp-box-${i}-${bi}`}>
                  <input type="number" min={1} value={b.page || 1}
                    onChange={(e) => updateBox(i, bi, { page: Number(e.target.value) })}
                    className="text-xs border border-navy/15 rounded px-2 py-1 font-mono"
                    title="page" />
                  <input type="number" step={0.1} value={b.x_pct}
                    onChange={(e) => updateBox(i, bi, { x_pct: Number(e.target.value) })}
                    className="text-xs border border-navy/15 rounded px-2 py-1 font-mono"
                    title="x %" />
                  <input type="number" step={0.1} value={b.y_pct}
                    onChange={(e) => updateBox(i, bi, { y_pct: Number(e.target.value) })}
                    className="text-xs border border-navy/15 rounded px-2 py-1 font-mono"
                    title="y %" />
                  <input type="number" step={0.1} value={b.w_pct}
                    onChange={(e) => updateBox(i, bi, { w_pct: Number(e.target.value) })}
                    className="text-xs border border-navy/15 rounded px-2 py-1 font-mono"
                    title="w %" />
                  <input type="number" step={0.1} value={b.h_pct}
                    onChange={(e) => updateBox(i, bi, { h_pct: Number(e.target.value) })}
                    className="text-xs border border-navy/15 rounded px-2 py-1 font-mono"
                    title="h %" />
                  <button onClick={() => removeBox(i, bi)}
                    disabled={p.boxes.length === 1}
                    className="text-xs px-2 py-1 rounded border border-rose-300 text-rose-700 hover:bg-rose-50 disabled:opacity-30"
                    data-testid={`pp-box-remove-${i}-${bi}`}>×</button>
                </div>
              ))}
              <button onClick={() => addBox(i)}
                className="text-[11px] px-2 py-1 rounded border border-navy/15 text-ink-soft hover:border-emerald-400 hover:text-emerald-800"
                data-testid={`pp-add-box-${i}`}>+ Add box</button>
            </div>
          </div>
        </div>
      ))}
      <button
        onClick={add}
        className="text-xs px-3 py-1.5 rounded border border-emerald-400 text-emerald-800 hover:bg-emerald-50 font-heading"
        data-testid="pp-add"
      >
        + Add preset
      </button>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Payments (Cashfree) API Settings tab
// ---------------------------------------------------------------------------
function PaymentsTab() {
  const [state, setState] = useState(null);
  const [appId, setAppId] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const [env, setEnv] = useState("SANDBOX");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = () => {
    api.get("/admin/settings/payments").then((r) => {
      const cf = r.data.cashfree || {};
      setState(cf);
      setAppId(cf.app_id || "");
      setEnv(cf.env || "SANDBOX");
      setSecretKey(""); // never show existing
    }).catch((e) => toast.error(formatApiError(e)));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings/payments", {
        app_id: appId.trim(),
        secret_key: secretKey.trim() || null,
        env: env,
      });
      toast.success("Cashfree credentials saved & live");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post("/admin/settings/payments/test");
      setTestResult(r.data);
      if (r.data.ok) toast.success("Cashfree connection verified ✓");
      else toast.error(`Cashfree rejected: ${r.data.reason}`);
    } catch (e) {
      setTestResult({ ok: false, reason: "error", message: formatApiError(e) });
      toast.error(formatApiError(e));
    } finally { setTesting(false); }
  };

  const copyWebhook = async () => {
    if (!state?.webhook_url) return;
    try {
      await navigator.clipboard.writeText(state.webhook_url);
      toast.success("Webhook URL copied");
    } catch { toast.error("Copy failed"); }
  };

  if (!state) return <Card><div className="text-ink-muted text-sm">Loading…</div></Card>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="settings-panel-payments">
      <div className="lg:col-span-2 space-y-6">
        <Card>
          <SectionHeader
            icon={CreditCard}
            title="Cashfree Payment Gateway"
            subtitle="Rotate App ID and Secret Key without editing .env. Changes take effect immediately."
          />
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2">
                <LabeledInput
                  label="App ID"
                  value={appId}
                  onChange={setAppId}
                  placeholder="TEST1234567890abcdef"
                  testId="cf-app-id-input"
                />
              </div>
              <div>
                <label className="block">
                  <div className="overline mb-1.5">Environment</div>
                  <select
                    value={env}
                    onChange={(e) => setEnv(e.target.value)}
                    className="w-full bg-white border border-navy/15 text-ink px-3 py-2.5 text-sm font-body focus:border-gold outline-none transition-all rounded-md"
                    data-testid="cf-env-select"
                  >
                    <option value="SANDBOX">SANDBOX</option>
                    <option value="TEST">TEST</option>
                    <option value="PRODUCTION">PRODUCTION</option>
                  </select>
                </label>
              </div>
            </div>
            <div>
              <div className="overline mb-1.5">
                Secret Key
                {state.secret_key_masked && (
                  <span className="ml-2 font-mono text-ink-muted normal-case tracking-normal">
                    current: <span className="text-ink">{state.secret_key_masked}</span>
                  </span>
                )}
              </div>
              <input
                type="password"
                value={secretKey}
                onChange={(e) => setSecretKey(e.target.value)}
                placeholder={state.secret_key_masked ? "Leave blank to keep existing" : "cfsk_ma_test_…"}
                className="w-full bg-white border border-navy/15 text-ink px-3 py-2.5 text-sm font-mono focus:border-gold focus:ring-2 focus:ring-gold/30 outline-none transition-all rounded-md"
                data-testid="cf-secret-input"
                autoComplete="new-password"
              />
              <div className="text-[11px] text-ink-muted mt-1">
                🔒 Stored in Mongo (system_settings.payments.cashfree). Only shown masked after save.
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 pt-2">
              <Button
                onClick={save}
                disabled={saving || !appId.trim()}
                className="bg-navy hover:bg-[#256E58] text-white"
                data-testid="cf-save-btn"
              >
                <Save size={14} className="mr-1.5" /> {saving ? "Saving…" : "Save credentials"}
              </Button>
              <Button
                variant="outline"
                onClick={testConnection}
                disabled={testing || !state.is_configured}
                data-testid="cf-test-btn"
              >
                {testing ? "Testing…" : "Test connection"}
              </Button>
              {state.source === "env" && (
                <span className="text-[11px] text-ink-muted font-mono">Currently loaded from .env — save to override.</span>
              )}
              {state.source === "db" && state.updated_at && (
                <span className="text-[11px] text-ink-muted font-mono">
                  Last rotated: {formatDateTime(state.updated_at)} by {state.updated_by}
                </span>
              )}
            </div>

            {testResult && (
              <div className={`mt-3 p-3 rounded border text-xs ${
                testResult.ok
                  ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                  : "border-rose-300 bg-rose-50 text-rose-900"
              }`} data-testid="cf-test-result">
                <div className="font-semibold mb-1">
                  {testResult.ok ? "✓ Cashfree accepted credentials" : `✗ ${testResult.reason || "Test failed"}`}
                </div>
                <div className="opacity-80 break-all">{testResult.message || `Cashfree HTTP ${testResult.cashfree_status}`}</div>
              </div>
            )}
          </div>
        </Card>
      </div>

      <div className="space-y-6">
        <Card>
          <SectionHeader icon={Plug} title="Status" />
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-ink-muted">Configured</span>
              <Pill tone={state.is_configured ? "success" : "danger"}>
                {state.is_configured ? "Yes" : "No"}
              </Pill>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-ink-muted">Environment</span>
              <Pill tone={state.env === "PRODUCTION" ? "gold" : "muted"}>{state.env}</Pill>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-ink-muted">Source</span>
              <Pill tone="muted">{state.source}</Pill>
            </div>
          </div>
        </Card>

        <Card>
          <SectionHeader icon={ExternalLink} title="Webhook URL"
            subtitle="Paste in Cashfree Dashboard → Developers → Webhooks" />
          <div className="flex items-stretch gap-2">
            <input
              readOnly
              value={state.webhook_url || ""}
              onFocus={(e) => e.target.select()}
              className="flex-1 px-3 py-2 border border-navy/15 rounded font-mono text-[11px] bg-white/70"
              data-testid="cf-webhook-url"
            />
            <Button variant="outline" onClick={copyWebhook} data-testid="cf-webhook-copy">
              <Copy size={14} />
            </Button>
          </div>
          <div className="text-[11px] text-ink-muted mt-2">
            Subscribe to: PAYMENT_SUCCESS_WEBHOOK, PAYMENT_FAILED_WEBHOOK, PAYMENT_USER_DROPPED_WEBHOOK.
          </div>
        </Card>

        <Card>
          <SectionHeader icon={AlertCircle} title="Security" />
          <div className="text-xs text-ink-muted space-y-2">
            <div>• Secret Key never leaves the server in cleartext.</div>
            <div>• Only <span className="font-mono">super_admin</span> can rotate keys.</div>
            <div>• Every rotation is recorded in the audit log.</div>
            <div>• If you leaked a key, generate a new one in Cashfree Dashboard and paste here — rotation is instant.</div>
          </div>
        </Card>
      </div>
      <div className="lg:col-span-3">
        <RazorpayCard onSaved={load} />
      </div>

    </div>
  );
}

// ---------------------------------------------------------------------------
// Razorpay Payment Gateway config — sits inside PaymentsTab.
// ---------------------------------------------------------------------------
function RazorpayCard({ onSaved }) {
  const [state, setState] = useState(null);
  const [keyId, setKeyId] = useState("");
  const [keySecret, setKeySecret] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = () => {
    api.get("/admin/settings/payments").then((r) => {
      const rzp = r.data.razorpay || {};
      setState(rzp);
      setKeyId(rzp.key_id || "");
      setEnabled(rzp.enabled !== false);
    }).catch((e) => toast.error(formatApiError(e)));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!keyId.startsWith("rzp_live_") && !keyId.startsWith("rzp_test_")) {
      toast.error("key_id must start with rzp_live_ or rzp_test_");
      return;
    }
    setSaving(true);
    try {
      await api.put("/admin/settings/payments/razorpay", {
        key_id: keyId.trim(),
        key_secret: keySecret.trim() || null,
        webhook_secret: webhookSecret.trim() || null,
        enabled,
      });
      toast.success("Razorpay credentials saved & live");
      setKeySecret(""); setWebhookSecret("");
      load();
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const testConnection = async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await api.post("/admin/settings/payments/razorpay/test");
      setTestResult(r.data);
      if (r.data.ok) toast.success(`Razorpay verified (${r.data.mode}) ✓`);
      else toast.error(`Razorpay rejected: ${r.data.reason}`);
    } catch (e) {
      setTestResult({ ok: false, reason: formatApiError(e) });
      toast.error(formatApiError(e));
    } finally { setTesting(false); }
  };

  const copyWebhook = async () => {
    if (!state?.webhook_url) return;
    try {
      await navigator.clipboard.writeText(state.webhook_url);
      toast.success("Webhook URL copied");
    } catch { toast.error("Copy failed"); }
  };

  if (!state) return null;
  const modeTone = state.mode === "LIVE" ? "text-emerald-800 bg-emerald-500/10"
                   : state.mode === "TEST" ? "text-amber-800 bg-amber-500/10"
                   : "text-ink-muted bg-navy/5";

  return (
    <Card>
      <SectionHeader
        icon={CreditCard}
        title="Razorpay Payment Gateway"
        subtitle="User-selectable alongside Cashfree at checkout. Both feed the same GST invoice pipeline."
      />
      <div className="flex items-center gap-3 mb-4">
        <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded ${modeTone}`} data-testid="rzp-mode-pill">
          {state.mode || "UNCONFIGURED"}{state.is_configured ? "" : " · not set"}
        </span>
        <label className="text-xs text-ink-soft flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="rzp-enabled-toggle" />
          Enabled at checkout
        </label>
      </div>
      <div className="space-y-3">
        <div>
          <label className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">key_id</label>
          <input value={keyId} onChange={(e) => setKeyId(e.target.value)} placeholder="rzp_live_… or rzp_test_…"
            className="w-full mt-1 px-3 py-2 border border-navy/15 rounded font-mono text-xs" data-testid="rzp-key-id" />
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">key_secret <span className="text-ink-faint">(blank = keep existing: <span className="font-mono">{state.key_secret_masked || "—"}</span>)</span></label>
          <input type="password" value={keySecret} onChange={(e) => setKeySecret(e.target.value)}
            placeholder="Paste to rotate; leave blank to keep current"
            className="w-full mt-1 px-3 py-2 border border-navy/15 rounded font-mono text-xs" data-testid="rzp-key-secret" />
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">webhook_secret <span className="text-ink-faint">(current: <span className="font-mono">{state.webhook_secret_masked || "—"}</span>)</span></label>
          <input type="password" value={webhookSecret} onChange={(e) => setWebhookSecret(e.target.value)}
            placeholder="Paste to rotate; leave blank to keep current"
            className="w-full mt-1 px-3 py-2 border border-navy/15 rounded font-mono text-xs" data-testid="rzp-webhook-secret" />
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={save} disabled={saving || !keyId} data-testid="rzp-save">
            {saving ? "Saving…" : "Save Razorpay Config"}
          </Button>
          <Button variant="outline" onClick={testConnection} disabled={testing || !state.is_configured} data-testid="rzp-test">
            {testing ? "Testing…" : "Test connection"}
          </Button>
          {testResult && (
            <span className={`text-xs font-mono ${testResult.ok ? "text-emerald-800" : "text-rose-800"}`}>
              {testResult.ok ? `OK · ${testResult.mode} · …${testResult.key_id_last4}` : `FAIL · ${testResult.reason}`}
            </span>
          )}
        </div>
        <div className="pt-2 border-t border-navy/10">
          <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1">Webhook URL — paste into Razorpay Dashboard → Settings → Webhooks</div>
          <div className="flex items-stretch gap-2">
            <input readOnly value={state.webhook_url || ""} onFocus={(e) => e.target.select()}
              className="flex-1 px-3 py-2 border border-navy/15 rounded font-mono text-[11px] bg-white/70" data-testid="rzp-webhook-url" />
            <Button variant="outline" onClick={copyWebhook} data-testid="rzp-webhook-copy">
              <Copy size={14} />
            </Button>
          </div>
          <div className="text-[10px] text-ink-muted mt-1">
            Subscribe events: payment.captured, payment.failed, payment.authorized, refund.processed.
          </div>
        </div>
      </div>
    </Card>
  );
}

